/**
 * Tidying a vocabulary: what is proposed, what it would cost, and what came of doing it.
 *
 * The wire half of the tidy tool. [Tags.kt][TagUse] says what the account *has*; this says
 * what could be folded together or thrown away, and it is the one place both sides of that
 * conversation are described.
 *
 * ── A plan is a value, not a sequence of calls ───────────────────────────────
 *
 * Everything below is data. The model that helps author part of a plan is asked for a
 * *mapping* rather than given tools to run, and that is the decision the whole feature rests
 * on: tool calls execute during generation, so preview-then-confirm would have to intercept,
 * buffer and refuse every one of them — building an execution engine in order to prevent
 * execution. A plan held as a value can be diffed, counted, drawn, unticked row by row and
 * sent back; a sequence of tool calls can only be replayed.
 *
 * It also means the model authors only *part* of it. Singular/plural pairs and singletons are
 * computed server-side by rules that do not vary, and arrive in the same shape as the model's
 * suggestions with [TidyMerge.source] saying which is which.
 *
 * ── Nothing here is trusted, in either direction ─────────────────────────────
 *
 * A [TidySelection] arriving at the server is re-validated against the live vocabulary before
 * anything is written — every `from` must be a tag the account owns, every `to` must survive
 * the plan, no chains, no cycles, and nothing that empties a bookmark. That is true whether the
 * selection came from this project's own modal or from a script, because the server cannot tell
 * them apart and must not need to. See the server's `TagTidy.kt`, where the rules live.
 *
 * A [TidyPlan] arriving at a *client* is untrusted in the other direction: a tag name is a
 * string somebody typed, so a surface that drew one as markup would make a tag an injection
 * vector. LunaPin's own dialog builds real DOM and assigns `textContent`; see the web's
 * `TidyTagsDialog`.
 *
 * @see ApiRoutes.TAGS_TIDY
 */
package se.soderbjorn.lunapin.clientserver

import kotlinx.serialization.Serializable

/**
 * Which pass proposed a change.
 *
 * Strings rather than an enum, for [SummaryLengths]' reason: an unknown value from a newer
 * server should reach a client as an unfamiliar label rather than as a parse failure that
 * blanks the whole plan.
 *
 * The value is **not** decoration. A row's source is what a reader uses to decide how much to
 * trust it, and the four differ enormously in how certain they are — which is why
 * [TidyMerge.preTicked] exists beside this rather than being derived from it by each client.
 */
object TidySources {
    /**
     * A singular/plural pair, found by rule. The most certain thing here.
     *
     * `#election` and `#elections` are one idea filed twice, and `hashPrefixed` only trims and
     * lower-cases, so nothing in the ordinary course of events ever merges them.
     */
    const val PLURAL: String = "plural"

    /** A tag used exactly once. Found by counting; certain about the count, not the value. */
    const val SINGLETON: String = "singleton"

    /**
     * A tag sharing a word with a much more used one — `vibe-coding` beside `coding`(80).
     *
     * The least certain of the deterministic passes and never pre-ticked: `mobile-coding` may
     * be a genuinely separate subject, and only the person who filed it knows.
     */
    const val STEM: String = "stem"

    /** The model suggested it. */
    const val MODEL: String = "model"
}

/**
 * Fold one tag into another, everywhere in this account.
 *
 * **A rename and a merge are the same operation**, which is why there is no second type for
 * renaming: renaming `#election` to `#elections` when `#elections` already exists *is* a merge,
 * and a client that had two verbs would have to decide which one it was doing by asking whether
 * the target happens to exist yet — a question whose answer can change between asking and
 * pressing.
 *
 * @property from the tag that will no longer exist. `#`-prefixed and lower-cased, exactly as
 *   stored.
 * @property to the tag it becomes. Must be a tag the account already owns or one that survives
 *   the rest of the plan — **a merge into an invented tag is normalised to a drop** rather than
 *   coining a name nobody chose. The server enforces that; see its `validateSelection`.
 * @property uses how many bookmarks carry [from] today. Shown on the row, for
 *   [TagUse.uses]' reason: two tags that look alike at a glance are told apart by their counts,
 *   and this is the number that says whether a merge is housekeeping or a decision.
 * @property source which pass proposed it — one of [TidySources].
 * @property preTicked whether a dialog should offer this already selected. False for anything a
 *   rule found but cannot vouch for: the low-confidence stem clusters, and the plural pair whose
 *   direction runs against the usual one (`#restaurants`(1) folding into `#restaurant`(5), where
 *   the plural is the rare spelling). A row that is not pre-ticked is still *shown*, because the
 *   suggestion is worth reading even when it is not worth taking by default.
 */
@Serializable
data class TidyMerge(
    val from: String,
    val to: String,
    val uses: Long = 0,
    val source: String = TidySources.PLURAL,
    val preTicked: Boolean = true,
)

/**
 * Remove one tag from every bookmark in this account.
 *
 * @property tag the tag, as stored.
 * @property uses how many bookmarks carry it. See [TidyMerge.uses].
 * @property source which pass proposed it — one of [TidySources].
 * @property preTicked as [TidyMerge.preTicked]. False for a singleton whose only bookmark would
 *   be left thinly filed, which is the case the epic's measurements say is rare and the case
 *   somebody would most regret.
 */
@Serializable
data class TidyDrop(
    val tag: String,
    val uses: Long = 0,
    val source: String = TidySources.SINGLETON,
    val preTicked: Boolean = true,
)

/**
 * A bookmark that would be left carrying no tags at all.
 *
 * Named rather than merely counted, and that is the whole point of the type: *one bookmark
 * would be left untagged* is a number somebody has to take on trust, and *this one, and here
 * is its title* is a fact they can check in three seconds. The list is capped by the server —
 * see its `MAX_NAMED_ORPHANS` — because a plan that orphaned four hundred bookmarks is one
 * nobody should be reading four hundred titles from.
 *
 * @property id the bookmark's own [Bookmark.id], so a client could open it.
 * @property title what the page called itself, or `""` — the "absent is ''" rule this whole
 *   wire follows.
 * @property url its address, which is what identifies it when the title is blank.
 */
@Serializable
data class TidyOrphan(
    val id: Long,
    val title: String = "",
    val url: String = "",
)

/**
 * What a set of merges and drops would do, **counted against the live database**.
 *
 * Never estimated and never computed by a client from a plan it holds: the numbers below need
 * the tag rows of every bookmark in the account, and a client that had those would be holding a
 * copy of the collection in order to draw a summary line. The server counts, the client draws.
 *
 * It is also why unticking a row is a request rather than arithmetic. Two merges into one
 * target overlap, a drop and a merge can touch the same bookmark, and the only place all of
 * that is knowable is where the rows are.
 *
 * @property tagsBefore how many distinct tags the account has now.
 * @property tagsAfter how many it would have. Not `tagsBefore - merged - dropped`: a merge whose
 *   target is itself dropped, or two merges that collapse into one surviving tag, make the
 *   arithmetic wrong in a way that is invisible until somebody checks.
 * @property tagRowsRemoved how many rows would go from `bookmark_tags` — the size of the change
 *   rather than the size of the vocabulary. A merge removes a row only where a bookmark already
 *   carried both tags; a drop removes one per bookmark.
 * @property merged how many merges are in the selection.
 * @property dropped how many drops are in the selection.
 * @property orphaned how many bookmarks would be left with no tags whatsoever.
 *
 *   **The number that turns a frightening button into a pressable one.** On the collection this
 *   epic was measured against, dropping all 685 singletons orphans exactly one bookmark — which
 *   is a fact about one database rather than a property of the feature, and is precisely why it
 *   is counted here every time rather than quoted.
 * @property orphanExamples up to a serverful of the bookmarks [orphaned] counts, so the sentence
 *   can be checked rather than believed. Empty when nothing would be orphaned.
 */
@Serializable
data class TidyImpact(
    val tagsBefore: Long = 0,
    val tagsAfter: Long = 0,
    val tagRowsRemoved: Long = 0,
    val merged: Long = 0,
    val dropped: Long = 0,
    val orphaned: Long = 0,
    val orphanExamples: List<TidyOrphan> = emptyList(),
)

/**
 * Everything a tidy dialog needs to open: what is proposed, and what taking all of it would do.
 *
 * @property merges every proposed fold, deterministic ones first and the model's after them.
 *   The order is the server's answer rather than a hint — it is the order the rows are read in,
 *   and two clients sorting differently would be two accounts of one plan.
 * @property drops every proposed removal, in the same arrangement.
 * @property bookmarks how many bookmarks the account has, so a summary can say *1182 tags
 *   across 1293 bookmarks* rather than a count with nothing to scale it against.
 * @property modelConsulted whether a model was asked at all. **False is an ordinary answer**,
 *   not a degraded one: the deterministic passes are pure SQL and string work, so a deployment
 *   with no key gets a smaller plan rather than an error, and there is no special case anywhere
 *   for the unconfigured server.
 * @property modelNote one sentence about why the model did not contribute, or `""` when it did
 *   or was never asked. Written to be shown as-is and **never carrying the provider's key or
 *   any part of it** — the same rule `AiTestResult` carries, enforced the same way.
 * @property impact what applying every pre-ticked row would do. The dialog's opening summary;
 *   unticking anything makes it stale, which is what [ApiRoutes.TAGS_TIDY_PREVIEW] is for.
 */
@Serializable
data class TidyPlan(
    val merges: List<TidyMerge> = emptyList(),
    val drops: List<TidyDrop> = emptyList(),
    val bookmarks: Long = 0,
    val modelConsulted: Boolean = false,
    val modelNote: String = "",
    val impact: TidyImpact = TidyImpact(),
) {
    /** Whether there is anything at all to show. A tidy collection plans to nothing. */
    val isEmpty: Boolean get() = merges.isEmpty() && drops.isEmpty()
}

/** One fold, as a selection sends it back. See [TidyMerge]. */
@Serializable
data class TidyPair(
    val from: String,
    val to: String,
)

/**
 * The part of a plan somebody actually wants.
 *
 * Sent to preview it and sent again to apply it, deliberately the same type: a preview that
 * described a different set from the one the button applies is the one bug this feature cannot
 * afford, and one type is how that is made unrepresentable rather than merely tested for.
 *
 * **It carries no counts and no confidence.** Everything a client was told in the [TidyPlan] is
 * the server's own answer and would be re-derived anyway; sending it back would invite a
 * caller to disagree about it. What travels is the two lists of tag names and nothing else.
 *
 * @property merges the folds to make.
 * @property drops the tags to remove everywhere.
 */
@Serializable
data class TidySelection(
    val merges: List<TidyPair> = emptyList(),
    val drops: List<String> = emptyList(),
)

/**
 * What a tidy did.
 *
 * @property applied the same shape the preview answered, recounted from what the transaction
 *   actually wrote — so a client that compares the two can see whether the collection moved
 *   under it between preview and press. `tagsBefore` is what the account had a moment ago.
 * @property canUndo whether there is now a snapshot to go back to. Always true after a tidy
 *   that changed anything, and it is stated rather than assumed because a caller drawing an
 *   *Undo last tidy* must not offer one for a tidy that turned out to be a no-op.
 */
@Serializable
data class TidyResult(
    val applied: TidyImpact = TidyImpact(),
    val canUndo: Boolean = false,
)

/**
 * What an undo restored.
 *
 * @property restored how many tag rows were put back, counted as they were written.
 * @property tags how many distinct tags the account has again — **counted only when something
 *   was restored**, and zero beside a zero [restored] rather than a fresh count of a
 *   collection nothing happened to. The pair is read together: `restored = 0` is "there was
 *   nothing to undo", and asking the database how many tags that account has anyway would be a
 *   query to fill in a field no caller would use.
 *
 *   **Not necessarily what it had before the tidy**, and the gap is honest rather than a bug: a
 *   bookmark deleted between the tidy and the undo takes its snapshot rows with it, by the same
 *   cascade that removes its live ones. Restoring a tag onto a bookmark that no longer exists is
 *   not a thing anybody could want.
 */
@Serializable
data class UndoTidyResult(
    val restored: Long = 0,
    val tags: Long = 0,
)

/**
 * What the Vocabulary panel says before anything is pressed, and whether there is a way back.
 *
 * Three counts and a boolean, answered by a route of its own rather than by planning: a plan
 * spends the operator's key, and a settings window that opened one on every load would be
 * charging somebody for a panel they were scrolling past.
 *
 * @property tags how many distinct tags the account has.
 * @property singletons how many of those are used exactly once — the number that says whether
 *   tidying is worth opening at all, and the headline finding of the epic this belongs to.
 * @property bookmarks how many bookmarks the account has, so the tag count has a scale.
 * @property canUndo whether the last tidy can still be undone. **One snapshot, replaced each
 *   time** — this is not a history, and there is no second step back.
 */
@Serializable
data class TidyStatus(
    val tags: Long = 0,
    val singletons: Long = 0,
    val bookmarks: Long = 0,
    val canUndo: Boolean = false,
)
